import { useMemo, useState } from "react";
import { Building2, LayoutGrid, Layers, List, Palette, Search, Shapes } from "lucide-react";
import useCatalogueProducts from "@/hooks/useCatalogueProducts";
import { cn } from "@/lib/utils";
import { HERO_IMAGE } from "@/lib/stockImages";
import PageHero from "@/components/layout/PageHero";
import ProductCard, {
  ProductRow,
  productColour,
  productDesign,
  productMaterial,
  productTypeLabel,
} from "@/components/ProductCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ALL = "all";
const BATCH = 24;

const versionLabel = (p) => p.versionName || p.catalogueReference || "";

const SORTS = {
  catalogue: { label: "Catalogue order", fn: null },
  az: { label: "Name (A–Z)", fn: (a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }) },
  za: { label: "Name (Z–A)", fn: (a, b) => b.name.localeCompare(a.name, undefined, { numeric: true }) },
};

const uniqueSorted = (values) => [...new Set(values.filter(Boolean))].sort((a, b) => a.localeCompare(b));

function FilterSelect({ label, icon: Icon, value, onChange, options, allLabel, testId }) {
  // Keep the current pick listed even when other filters no longer match it.
  const items = value !== ALL && !options.includes(value) ? [value, ...options] : options;
  return (
    <div className="min-w-0">
      <p className="text-xs text-muted-foreground mb-1.5">{label}</p>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testId} className="rounded-lg bg-card">
          <div className="flex items-center gap-2 min-w-0">
            <Icon className="h-4 w-4 text-muted-foreground shrink-0" strokeWidth={1.75} />
            <SelectValue />
          </div>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{allLabel}</SelectItem>
          {items.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

export default function ProductsPage() {
  const [company, setCompany] = useState(ALL);
  const [version, setVersion] = useState(ALL);
  const [type, setType] = useState(ALL);
  const [material, setMaterial] = useState(ALL);
  const [colour, setColour] = useState(ALL);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("catalogue");
  const [view, setView] = useState("grid");
  const [shown, setShown] = useState(BATCH);

  const { products, cataloguesLoading, productsLoading } = useCatalogueProducts();
  const isLoading = cataloguesLoading || productsLoading;

  // Each filter's options come from the products the other filters leave, so
  // picking a company narrows versions, types and colours to what it actually has.
  const matches = (p, except) =>
    (except === "company" || company === ALL || p.companyName === company) &&
    (except === "version" || version === ALL || versionLabel(p) === version) &&
    (except === "type" || type === ALL || productTypeLabel(p) === type) &&
    (except === "material" || material === ALL || productMaterial(p) === material) &&
    (except === "colour" || colour === ALL || productColour(p) === colour);

  const options = useMemo(
    () => ({
      company: uniqueSorted(products.filter((p) => matches(p, "company")).map((p) => p.companyName)),
      version: uniqueSorted(products.filter((p) => matches(p, "version")).map(versionLabel)),
      type: uniqueSorted(products.filter((p) => matches(p, "type")).map(productTypeLabel)),
      material: uniqueSorted(products.filter((p) => matches(p, "material")).map(productMaterial)),
      colour: uniqueSorted(products.filter((p) => matches(p, "colour")).map(productColour)),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [products, company, version, type, material, colour],
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const list = products.filter(
      (p) =>
        matches(p) &&
        (!term || [p.name, p.shopReference, p.companyName, productDesign(p), productColour(p)].some((v) => v?.toLowerCase().includes(term))),
    );
    return SORTS[sort].fn ? [...list].sort(SORTS[sort].fn) : list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, company, version, type, material, colour, search, sort]);

  const visible = filtered.slice(0, shown);
  const filtersActive = company !== ALL || version !== ALL || type !== ALL || material !== ALL || colour !== ALL || search;

  const set = (fn) => (v) => {
    fn(v);
    setShown(BATCH);
  };
  const clearFilters = () => {
    [setCompany, setVersion, setType, setMaterial, setColour].forEach((fn) => fn(ALL));
    setSearch("");
    setShown(BATCH);
  };

  return (
    <div data-testid="products-page" className="space-y-6">
      <PageHero
        kpiOverlap={false}
        image={HERO_IMAGE}
        tagline="Premium fabrics for beautiful living spaces."
        title="Products"
        subtitle="Every fabric and finish across all your catalogues, in one place."
      />

      <section className="bg-card border border-border rounded-2xl p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
          <FilterSelect testId="products-company-filter" label="Catalogue company" icon={Building2} value={company} allLabel="All companies"
            options={options.company} onChange={set((v) => { setCompany(v); setVersion(ALL); })} />
          <FilterSelect testId="products-version-filter" label="Catalogue version" icon={Layers} value={version} allLabel="All versions"
            options={options.version} onChange={set(setVersion)} />
          <FilterSelect testId="products-type-filter" label="Product type" icon={LayoutGrid} value={type} allLabel="All types"
            options={options.type} onChange={set(setType)} />
          <FilterSelect testId="products-material-filter" label="Fabric" icon={Shapes} value={material} allLabel="All fabrics"
            options={options.material} onChange={set(setMaterial)} />
          <FilterSelect testId="products-colour-filter" label="Colour" icon={Palette} value={colour} allLabel="All colours"
            options={options.colour} onChange={set(setColour)} />
        </div>
      </section>

      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <p className="font-medium text-foreground shrink-0" data-testid="products-count">
          {isLoading ? "Loading products…" : `${filtered.length} product${filtered.length === 1 ? "" : "s"}`}
        </p>
        {filtersActive && (
          <button data-testid="products-clear-filters" onClick={clearFilters} className="text-sm font-medium text-accent hover:underline text-left">
            Clear filters
          </button>
        )}
        <div className="flex flex-col sm:flex-row gap-3 md:ml-auto">
          <div className="relative sm:w-72">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              data-testid="products-search-input"
              value={search}
              onChange={(e) => set(setSearch)(e.target.value)}
              placeholder="Search name, code, design…"
              className="w-full rounded-full border border-border bg-card pl-10 pr-4 py-2.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>
          <div className="flex gap-3">
            <div className="flex items-center gap-1 rounded-lg border border-border bg-card p-0.5" role="group" aria-label="View">
              {[["grid", LayoutGrid], ["list", List]].map(([key, Icon]) => (
                <button
                  key={key}
                  aria-label={`${key} view`}
                  aria-pressed={view === key}
                  data-testid={`products-view-${key}`}
                  onClick={() => setView(key)}
                  className={cn("p-2 rounded-md transition-colors", view === key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary")}
                >
                  <Icon className="h-4 w-4" />
                </button>
              ))}
            </div>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger data-testid="products-sort" className="w-[200px] rounded-lg bg-card">
                <span className="text-muted-foreground mr-1">Sort:</span> <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(SORTS).map(([key, s]) => <SelectItem key={key} value={key}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {view === "grid" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-5">
          {visible.map((p) => <ProductCard key={p.id} p={p} />)}
        </div>
      ) : (
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          <ul className="divide-y divide-border">
            {visible.map((p) => <ProductRow key={p.id} p={p} />)}
          </ul>
        </div>
      )}

      {!isLoading && filtered.length === 0 && (
        <div className="bg-card border border-dashed border-border rounded-2xl py-14 text-center">
          <p className="text-sm text-muted-foreground">
            {products.length === 0 ? "No products yet — upload a catalogue to get started." : "No products match these filters."}
          </p>
          {filtersActive && <button onClick={clearFilters} className="mt-2 text-sm font-medium text-accent hover:underline">Clear filters</button>}
        </div>
      )}

      {filtered.length > shown && (
        <div className="flex flex-col items-center gap-2 pt-2">
          <p className="text-xs text-muted-foreground">Showing {visible.length} of {filtered.length}</p>
          <button
            data-testid="products-load-more"
            onClick={() => setShown((n) => n + BATCH)}
            className="rounded-full border border-border bg-card px-6 py-2.5 text-sm font-medium text-foreground hover:border-primary hover:text-primary transition-colors"
          >
            Load more products
          </button>
        </div>
      )}
    </div>
  );
}
